import { useToast } from '@/composables/useToast';
import { Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  IMassUploadFile,
  IRenditionFile,
  IUploadRecord,
} from '../MassUpload.types';

type FileMatcher = (
  uploadRecord: IUploadRecord | IRenditionFile,
  file: IMassUploadFile,
) => boolean;

export function useFilesMapping(
  massUploadFiles: Ref<IMassUploadFile[]>,
  uploadRecords: Ref<IUploadRecord[]>,
  isReadingZipFile: Ref<boolean>,
) {
  const { createWarningToast } = useToast();
  const { t } = useI18n();

  watch(
    uploadRecords,
    () => {
      if (massUploadFiles.value.length) {
        mapFilesToFilePaths(true);
      } else mapFilesToFilePaths(false);
    },
    { deep: true },
  );

  watch(
    massUploadFiles,
    () => {
      if (uploadRecords.value.length) {
        mapFilesToFilePaths(true);
      } else mapFilesToFilePaths(false);
    },
    { deep: true },
  );

  const fileMatchers: FileMatcher[] = [
    matchFilepath,
    matchFilepathWithoutDirectory,
    matchFileName,
  ];

  function mapFilesToFilePaths(warnAboutNoFilesMatched = false) {
    let isAtLeastOneFileFound = false;
    massUploadFiles.value.forEach((file) => {
      const recordWithMatchingMainFile = uploadRecords.value.find((record) => {
        /**
         * The record can already have a file if **isNoMetadataUploadMode**
         * is on.
         */
        if (record.file) {
          isAtLeastOneFileFound = true;
          return;
        }

        for (const matcher of fileMatchers) {
          if (matcher(record, file)) {
            isAtLeastOneFileFound = true;
            return true;
          }
        }
      });
      if (recordWithMatchingMainFile) {
        recordWithMatchingMainFile.file = file.file;
      }
      const recordWithMatchingRenditionFile = uploadRecords.value.find(
        (record) => {
          if (!record.renditionFile) return;
          if (record.renditionFile.file) {
            isAtLeastOneFileFound = true;
            return;
          }

          for (const matcher of fileMatchers) {
            if (matcher(record.renditionFile, file)) {
              isAtLeastOneFileFound = true;
              return true;
            }
          }
        },
      );
      if (recordWithMatchingRenditionFile) {
        recordWithMatchingRenditionFile.renditionFile!.file = file.file;
      }
    });

    if (warnAboutNoFilesMatched && !isAtLeastOneFileFound) {
      createWarningToast(t('documentUpload.warnings.noFilesFound'));
    }
    isReadingZipFile.value = false;
  }

  /**
   * Looking for a file in the ZIP matching the provided file path
   * with directory name (ZIP name) included
   * @example
   * ```
   * ZIP path: zip-directory-name/some-file.pdf
   * file path from CSV: zip-directory-name/some-file.pdf
   * ```
   */
  function matchFilepath(
    uploadRecord: IUploadRecord | IRenditionFile,
    file: IMassUploadFile,
  ): boolean {
    return (
      uploadRecord.metadata?.Filepath?.toLowerCase().trim().normalize('NFC') ===
      file.path.toLowerCase().trim().normalize('NFC')
    );
  }

  /**
   * Looking for a file in the ZIP ignoring the directory name
   * (ZIP name)
   * @example
   * ```
   * ZIP path: zip-directory-name/some-file.pdf
   * file path from CSV: some-file.pdf
   * ```
   */
  function matchFilepathWithoutDirectory(
    uploadRecord: IUploadRecord | IRenditionFile,
    file: IMassUploadFile,
  ): boolean {
    const pathWithoutDirectory = getPathWithoutDirectory(file);
    return (
      uploadRecord?.metadata?.Filepath?.toLowerCase()
        .trim()
        .normalize('NFC') ===
      pathWithoutDirectory.toLowerCase().trim().normalize('NFC')
    );
  }

  /**
   * If path was not supplied or does not match any file, we are
   * looking for a file lying directly in the ZIP file with
   * a matching name
   * @example
   * ```
   * ZIP path: zip-directory-name/some-file.pdf
   * file name from CSV: some-file.pdf
   * ```
   */
  function matchFileName(
    uploadRecord: IUploadRecord | IRenditionFile,
    file: IMassUploadFile,
  ): boolean {
    const pathWithoutDirectory = getPathWithoutDirectory(file);
    return (
      uploadRecord?.metadata?.Name?.toLowerCase().trim().normalize('NFC') ===
      pathWithoutDirectory.toLowerCase().trim().normalize('NFC')
    );
  }

  function getPathWithoutDirectory(file: IMassUploadFile): string {
    const pathArray = file.path.split('/');
    pathArray.shift();
    return pathArray.join('/');
  }
}
